import React, { useState, useMemo } from 'react';
import { FlattenedNode } from '../types/figma';

interface ComponentTreeSidebarProps {
  nodes: FlattenedNode[];
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  loading: boolean;
  onSelectNode: (node: FlattenedNode) => void;
  onHoverNode: (nodeId: string | null) => void;
}

const ComponentTreeSidebar: React.FC<ComponentTreeSidebarProps> = ({
  nodes,
  selectedNodeId,
  hoveredNodeId,
  loading,
  onSelectNode,
  onHoverNode,
}) => {
  const [search, setSearch] = useState('');

  const filteredNodes = useMemo(() => {
    if (!search.trim()) return nodes;
    const lower = search.toLowerCase();
    return nodes.filter(n => n.name.toLowerCase().includes(lower) || n.type.toLowerCase().includes(lower));
  }, [nodes, search]);

  const typeIcons: Record<string, string> = {
    FRAME: 'F',
    GROUP: 'G',
    COMPONENT: 'C',
    COMPONENT_SET: 'CS',
    INSTANCE: 'I',
    TEXT: 'T',
    RECTANGLE: 'R',
    ELLIPSE: 'E',
    VECTOR: 'V',
  };

  return (
    <div
      style={{
        width: '260px',
        borderRight: '1px solid #ddd',
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
      }}
    >
      <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search nodes..."
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <div style={{ flex: 1, overflow: 'auto', fontSize: '13px' }}>
        {loading ? (
          <div style={{ padding: '16px', color: '#888', fontSize: '13px' }}>
            Loading components...
          </div>
        ) : (
          filteredNodes.map(node => (
            <div
              key={node.id}
              onClick={() => onSelectNode(node)}
              onMouseEnter={() => onHoverNode(node.id)}
              onMouseLeave={() => onHoverNode(null)}
              style={{
                padding: '4px 8px',
                paddingLeft: `${8 + node.depth * 16}px`,
                cursor: 'pointer',
                background:
                  selectedNodeId === node.id
                    ? '#d4e6f9'
                    : hoveredNodeId === node.id
                      ? '#eef5fd'
                      : 'transparent',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '20px',
                  height: '16px',
                  lineHeight: '16px',
                  textAlign: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: '#888',
                  background: '#eee',
                  borderRadius: '2px',
                  flexShrink: 0,
                }}
              >
                {typeIcons[node.type] || '?'}
              </span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={`${node.name} (${node.type})`}
              >
                {node.name}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ComponentTreeSidebar;
